import React, { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { XMarkIcon, ArrowUpTrayIcon, TrashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { selectNotifications, removeNotification } from '../store/notificationSlice'

// Auto-dismiss timing constants
const DISMISS_DURATION = 3000 // 3 seconds
const UPDATE_INTERVAL = 50 // 50ms for smooth animation
const PROGRESS_DECREMENT = 100 / (DISMISS_DURATION / UPDATE_INTERVAL)

// Message toasts that should auto-dismiss on a timer. Progress toasts
// ('upload'/'delete') are excluded — they clear themselves on completion.
const AUTO_DISMISS_TYPES = new Set(['success', 'error', 'warning', 'info'])
const shouldAutoDismiss = (type) => AUTO_DISMISS_TYPES.has(type)

const NotificationToast = () => {
  const notifications = useSelector(selectNotifications)
  const dispatch = useDispatch()
  const [autoDismissProgress, setAutoDismissProgress] = useState({})
  const processedNotifications = useRef(new Set())
  const timersRef = useRef({})
  const intervalsRef = useRef({})

  // Helper function to clear timers for a specific notification
  const clearNotificationTimers = (notificationId) => {
    const timers = timersRef.current
    const intervals = intervalsRef.current
    const processed = processedNotifications.current

    if (timers[notificationId]) {
      clearTimeout(timers[notificationId])
      delete timers[notificationId]
    }
    if (intervals[notificationId]) {
      clearInterval(intervals[notificationId])
      delete intervals[notificationId]
    }
    processed.delete(notificationId)
  }

  // Auto remove success/error notifications after 3 seconds with countdown
  useEffect(() => {
    const timers = timersRef.current
    const intervals = intervalsRef.current
    const processed = processedNotifications.current

    notifications.forEach(notification => {
      // Only process notifications that haven't been processed yet
      if (shouldAutoDismiss(notification.type) &&
          !processed.has(notification.id)) {

        // Mark as processed
        processed.add(notification.id)

        // Initialize progress
        setAutoDismissProgress(prev => ({ ...prev, [notification.id]: 100 }))

        // Update progress every 50ms for smooth animation
        intervals[notification.id] = setInterval(() => {
          setAutoDismissProgress(prev => {
            const currentProgress = prev[notification.id] || 100
            const newProgress = currentProgress - PROGRESS_DECREMENT
            return { ...prev, [notification.id]: Math.max(0, newProgress) }
          })
        }, UPDATE_INTERVAL)

        // Remove notification after 3 seconds
        timers[notification.id] = setTimeout(() => {
          // Clean up progress state before dispatching removal to avoid race condition
          setAutoDismissProgress(prev => {
            const newProgress = { ...prev }
            delete newProgress[notification.id]
            return newProgress
          })
          dispatch(removeNotification(notification.id))
        }, DISMISS_DURATION)
      }
    })

    // Cleanup timers for notifications that no longer exist
    return () => {
      const currentNotificationIds = new Set(notifications.map(n => n.id))

      // Clean up timers for removed notifications
      Object.keys(timers).forEach(id => {
        if (!currentNotificationIds.has(id)) {
          clearTimeout(timers[id])
          clearInterval(intervals[id])
          delete timers[id]
          delete intervals[id]
          processed.delete(id)

          // Also clean up progress state to prevent memory leak
          setAutoDismissProgress(prev => {
            const newProgress = { ...prev }
            delete newProgress[id]
            return newProgress
          })
        }
      })
    }
  }, [notifications, dispatch])

  // Cleanup all timers on component unmount
  useEffect(() => {
    const timers = timersRef.current
    const intervals = intervalsRef.current
    const processed = processedNotifications.current

    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer))
      Object.values(intervals).forEach(interval => clearInterval(interval))
      timersRef.current = {}
      intervalsRef.current = {}
      processed.clear()
    }
  }, [])

  if (notifications.length === 0) return null

  const getIcon = (type) => {
    switch (type) {
      case 'upload':
        return <ArrowUpTrayIcon className="h-6 w-6" />
      case 'delete':
        return <TrashIcon className="h-6 w-6" />
      case 'success':
        return <CheckCircleIcon className="h-6 w-6" />
      case 'error':
        return <XCircleIcon className="h-6 w-6" />
      default:
        return null
    }
  }

  const getNotificationClasses = (type) => {
    switch (type) {
      case 'upload':
        return 'notification-upload'
      case 'delete':
        return 'notification-delete'
      case 'success':
        return 'notification-success'
      case 'error':
        return 'notification-error'
      case 'warning':
        return 'notification-warning'
      case 'info':
      default:
        return 'notification-info'
    }
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`notification-toast ${getNotificationClasses(notification.type)}`}
        >
            <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1">
              <div className="notification-icon">
                {getIcon(notification.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium break-words" style={{ color: 'var(--color-text-primary)' }}>
                  {notification.message}
                </p>
                {notification.fileName && (
                  <p className="text-xs break-words" style={{ color: 'var(--color-text-secondary)' }}>
                    {notification.fileName}
                  </p>
                )}
                
                {/* Progress bar for upload/delete operations */}
                {(notification.type === 'upload' || notification.type === 'delete') && (
                  <div className="mt-2">
                    <div className="notification-progress-bar">
                      <div
                        className="notification-progress-fill"
                        style={{ width: `${notification.progress}%` }}
                      />
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                      {Math.round(notification.progress)}%
                    </p>
                  </div>
                )}

                {/* Auto-dismiss countdown for message notifications */}
                {shouldAutoDismiss(notification.type) && autoDismissProgress[notification.id] !== undefined && (
                  <div className="mt-2">
                    <div className="notification-progress-bar">
                      <div
                        className="notification-progress-fill"
                        style={{
                          width: `${autoDismissProgress[notification.id]}%`,
                          transition: 'width 50ms linear'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <button
              onClick={() => {
                clearNotificationTimers(notification.id)
                dispatch(removeNotification(notification.id))
              }}
              className="ml-2 p-1 rounded-full transition-colors"
              style={{
                color: 'var(--color-text-muted)',
                '&:hover': {
                  backgroundColor: 'var(--color-fill)'
                }
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--color-fill)'
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent'
              }}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

export default NotificationToast
